import React, { useState } from 'react';
import styled from 'styled-components';
import { Flex } from 'components/common/styled/common-styled';
import { Session } from 'types/listings';
import { Button } from 'components/theme/Button/Button';
import { currencyFormat, isImageRatioGreaterOne } from 'utils';
import { useTranslation } from 'react-i18next';
import { ResourceParagraph } from 'components/theme/Typography/ResourceTypography';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { IPriceTier } from 'types/experience.types';
import type { ITax } from 'types/experience.types';
import { PublicListingDetailsV3 } from 'types/listingV3';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import { isContinuousScheduleType, ResourceGroupV3 } from '@kouto/types';
import useContinuousStartingPrice from 'features/Reserve/hooks/useContinuousStartingPrice';
import { hasBookableAvailability } from 'features/Reserve/utils/groupAvailability';
import { CollectionTitle } from './CollectionHero';

type PriceTier = IPriceTier & {
  id: string;
};

interface MobileMappedCollectionPageProps {
  listing: PublicListingDetailsV3;
  sessionsByGroup: Record<string, Session[]>;
  onReserve: (groupId: string) => void;
}

interface GroupLabelProps {
  resourceGroup: ResourceGroupV3;
  scheduleType: PublicListingDetailsV3['scheduleType'];
  sessions: Session[];
}

const GroupLabel = ({
  resourceGroup,
  scheduleType,
  sessions,
}: GroupLabelProps) => {
  const currency = useBrandCurrency();
  const { t: translate } = useTranslation();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();
  const continuousPrice = useContinuousStartingPrice({
    group: resourceGroup,
    scheduleType,
    spans: sessions,
  });

  const priceTiers = sessions
    .flatMap<PriceTier>((e) => (e ? e.priceTiers ?? [] : []))
    .sort((a, b) => a.price - b.price);
  const startingPrice = priceTiers[0]?.price || 0;
  const experience = resourceGroup.experiences?.[0];
  // A flexible or multi-day group's tier rate is not bookable on its own; its shortest booking is.
  const advertisedPrice = isContinuousScheduleType(scheduleType)
    ? continuousPrice?.amount
    : calculateAdvertisedTierPrice(
        startingPrice,
        (experience?.taxes as unknown as ITax[]) ?? [],
      );
  const displayedCapacity = Number(experience?.displayCapacity ?? 0);

  if (!hasBookableAvailability(sessions)) {
    return <>{translate('unavailable')}</>;
  }

  let label = '';
  if (displayedCapacity > 0) {
    label = `${displayedCapacity} ${
      displayedCapacity === 1 ? translate('person') : translate('people')
    }`;
  }
  if (displayedCapacity > 0 && currency && advertisedPrice !== undefined) {
    label += ' • ';
  }
  if (currency && advertisedPrice !== undefined) {
    label += `${translate('from')} ${currencyFormat(currency)(
      advertisedPrice,
    )}${continuousPrice ? ` ${continuousPrice.unitLabel}` : ''}`;
  }
  return <>{label}</>;
};

const MobileMappedCollectionPage = ({
  listing,
  sessionsByGroup,
  onReserve,
}: MobileMappedCollectionPageProps) => {
  const [isSixteenNineRatio, setIsSixteenNineRatio] = useState(false);
  const { t: translate } = useTranslation();

  const onImgLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    if (e.target instanceof HTMLImageElement) {
      setIsSixteenNineRatio(
        isImageRatioGreaterOne(e.target.naturalWidth, e.target.naturalHeight),
      );
    }
  };

  return (
    <>
      <Container
        direction="column"
        alignItem="flex-start"
        justifyContent="flex-start"
        gap={16}
      >
        <Flex
          direction="column"
          gap={24}
          alignItem="flex-start"
          justifyContent="flex-start"
          style={{ width: '100%' }}
        >
          {listing.resourceGroups?.map((resourceGroup) => (
            <GroupRow key={resourceGroup.id} disabled={false}>
              <ResourceGroupImage
                src={
                  resourceGroup.medias.find((media) => media.kind === 'gallery')
                    ?.links[0]?.url
                }
                alt={resourceGroup.title ?? undefined}
                isSixteenNineRatio={isSixteenNineRatio}
                onLoad={onImgLoad}
              />
              <Flex
                direction="column"
                gap={3}
                alignItem="flex-start"
                justifyContent="flex-start"
                style={{
                  maxWidth: 'calc(100% - 164px)',
                }}
              >
                <ResourceParagraphHeading
                  className="mapped-collection-page__resource-title"
                  lineHeight="normal"
                  fontWeight="500"
                >
                  {resourceGroup.title}
                </ResourceParagraphHeading>

                <ResourceParagraphDetails
                  className="mapped-collection-page__resource-details"
                  fontSize="12px"
                  fontWeight="400"
                  lineHeight="normal"
                >
                  <GroupLabel
                    resourceGroup={resourceGroup}
                    scheduleType={listing.scheduleType}
                    sessions={sessionsByGroup[resourceGroup.id] ?? []}
                  />
                </ResourceParagraphDetails>
                <StyledReserveButton
                  className="mapped-collection-page__reserve-button"
                  onClick={() => onReserve(resourceGroup.id)}
                >
                  {translate('reserveNow')}
                </StyledReserveButton>
              </Flex>
            </GroupRow>
          ))}
        </Flex>
      </Container>
      <CollectionTitle allowMultipleLines>{listing.title}</CollectionTitle>
    </>
  );
};

const Container = styled(Flex)`
  padding: 4px;
`;

const GroupRow = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  place-items: center flex-start;

  & > *:first-child {
    width: 140px;
    margin-right: 24px;
    flex-shrink: 0;
  }

  ${({ disabled }) =>
    disabled
      ? `
    &:before {
      content: ' ';
      cursor: not-allowed;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background-color: var(--way-palette-black-20);
      pointer-events: all;
    }
  `
      : ''}
`;

const ResourceGroupImage = styled.img<{ isSixteenNineRatio: boolean }>`
  width: 100%;
  height: 93px;
  object-fit: cover;
  object-position: center;
`;

const ResourceParagraphHeading = styled(ResourceParagraph)`
  && {
    color: var(--way-colors-primaryTextColor);
  }
`;

const ResourceParagraphDetails = styled(ResourceParagraph)`
  && {
    margin-bottom: 3px;
  }
`;

const StyledReserveButton = styled(Button)`
  padding: 12px 20px;
  font-size: 12px;
  border: 0;
`;

export default MobileMappedCollectionPage;
